import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, UserCheck, Briefcase, Mail, Sparkles, Zap, ShieldCheck, Eye, Handshake, SlidersHorizontal } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useIsMobile } from '../lib/useIsMobile';
export function TalentPage() {
  const isMobile = useIsMobile();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    title: 'Career Development',
    description: 'Strategic guidance to build your personal brand and ensure long-term growth in an ever-changing digital landscape.',
    icon: TrendingUp,
    color: 'from-purple-400 to-pink-400',
    shadow: 'rgba(167,139,250,0.4)'
  }, {
    title: 'Content Strategy',
    description: 'Creative direction and data-driven content planning to maximize engagement and grow your audience authentically.',
    icon: Briefcase,
    color: 'from-blue-400 to-cyan-400',
    shadow: 'rgba(96,165,250,0.4)'
  }, {
    title: 'Partnership Management',
    description: 'End-to-end management of brand deals, from negotiation and contracting to execution and reporting.',
    icon: UserCheck,
    color: 'from-green-400 to-emerald-400',
    shadow: 'rgba(110,231,183,0.4)'
  }];
  const bounceTransition = {
    type: 'spring',
    stiffness: 260,
    damping: 20
  };
  return <Layout data-id="element-230">
      {/* Hero Section - Added more top padding */}
      <div className="pt-40 md:pt-48 pb-12 md:pb-20 px-4 md:px-8 max-w-7xl mx-auto relative overflow-hidden" data-id="element-231">
        <motion.div initial={{
        y: 50,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={bounceTransition} className="mb-12 md:mb-20 relative z-10" data-id="element-232">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-fredoka font-bold text-gray-800 mb-6 leading-tight" data-id="element-233">
            Talent{' '}
            <motion.span className="inline-block px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-3xl border-4 border-gray-800 shadow-[6px_6px_0px_rgba(45,55,72,1)]" animate={{
            rotate: [-2, 2, -2],
            y: [0, -8, 0]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-234">
              Management
            </motion.span>
          </h1>
          <p className="text-lg md:text-2xl text-gray-700 max-w-2xl font-bold leading-relaxed" data-id="element-235">
            We empower talents to build sustainable careers. Our 360-degree
            approach covers everything from day-to-day management to long-term
            strategic planning.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative z-10" data-id="element-236">
          {services.map((service, index) => <motion.div key={service.title} initial={{
          y: 50,
          opacity: 0,
          rotate: -3
        }} whileInView={{
          y: 0,
          opacity: 1,
          rotate: 0
        }} viewport={{
          once: true
        }} transition={{
          ...bounceTransition,
          delay: index * 0.1
        }} className="group relative" data-id="element-237">
              <div className="relative p-6 md:p-8 rounded-3xl bg-white border-4 border-gray-800 transition-all duration-300 hover:translate-x-[-4px] hover:translate-y-[-4px]" style={{
            boxShadow: `12px 12px 0px ${service.shadow}`
          }} onMouseEnter={e => {
            e.currentTarget.style.boxShadow = `16px 16px 0px ${service.shadow}`;
          }} onMouseLeave={e => {
            e.currentTarget.style.boxShadow = `12px 12px 0px ${service.shadow}`;
          }} data-id="element-238">
                <div className={`mb-6 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} border-3 border-gray-800 text-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-300`} data-id="element-239">
                  <service.icon className="w-7 h-7" data-id="element-240" />
                </div>
                <h3 className="text-2xl md:text-3xl font-fredoka font-bold text-gray-800 mb-4 group-hover:scale-105 transition-transform" data-id="element-241">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed font-medium text-base" data-id="element-242">
                  {service.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 relative overflow-hidden" data-id="element-243">
        <div className="max-w-5xl mx-auto relative" data-id="element-244">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} whileInView={{
          scale: 1,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={bounceTransition} className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-245">
            {/* Stat 1 */}
            <div className="relative p-8 md:p-12 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(167,139,250,0.4)] hover:shadow-[16px_16px_0px_rgba(167,139,250,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-246">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-400 border-3 border-gray-800 text-white" data-id="element-247">
                <Zap className="w-6 h-6" data-id="element-248" />
              </div>
              <h3 className="text-5xl md:text-6xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-249">
                Long-Term
              </h3>
              <p className="text-gray-600 font-bold text-sm tracking-wide uppercase" data-id="element-250">
                Collaborations Generated
              </p>
            </div>

            {/* Stat 2 */}
            <div className="relative p-8 md:p-12 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(110,231,183,0.4)] hover:shadow-[16px_16px_0px_rgba(110,231,183,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-251">
              <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-400 border-3 border-gray-800 text-white" data-id="element-252">
                <TrendingUp className="w-6 h-6" data-id="element-253" />
              </div>
              <h3 className="text-5xl md:text-6xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-254">
                Six Figure
              </h3>
              <p className="text-gray-600 font-bold text-sm tracking-wide uppercase" data-id="element-255">
                Revenue
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transparency & Trust Section */}
      <section className="py-16 md:py-28 px-4 md:px-8 relative overflow-hidden" data-id="element-256">
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-257">
          <motion.div initial={{
          y: 40,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={bounceTransition} className="text-center mb-12 md:mb-16" data-id="element-258">
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-200 to-teal-200 border-3 border-gray-800 text-gray-800 font-bold text-sm tracking-wider uppercase shadow-[4px_4px_0px_rgba(45,55,72,0.3)] mb-8" data-id="element-259">
              <ShieldCheck className="w-5 h-5" data-id="element-260" />
              Our Promise
            </span>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-fredoka font-bold text-gray-800 mb-6 leading-tight" data-id="element-261">
              Built on{' '}
              <motion.span className="inline-block px-5 py-2 md:px-6 md:py-3 bg-gradient-to-r from-emerald-400 to-teal-400 text-white rounded-3xl border-4 border-gray-800 shadow-[6px_6px_0px_rgba(45,55,72,1)]" animate={{
              rotate: [-2, 2, -2],
              y: [0, -6, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-262">
                Transparency
              </motion.span>{' '}
              & Trust
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto font-medium leading-relaxed" data-id="element-263">
              Our commitment starts with full transparency and trust — the very
              first factor we rely upon with every talent we work with. You'll
              always know exactly what's happening, every step of the way.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-264">
            {/* Tailored Model */}
            <motion.div initial={{
            x: -40,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            ...bounceTransition,
            delay: 0.1
          }} className="group" data-id="element-265">
              <div className="relative p-6 md:p-8 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(110,231,183,0.4)] hover:shadow-[16px_16px_0px_rgba(110,231,183,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-266">
                <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-400 border-3 border-gray-800 text-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" data-id="element-267">
                  <SlidersHorizontal className="w-7 h-7" data-id="element-268" />
                </div>
                <h3 className="text-2xl md:text-3xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-269">
                  Tailored to You
                </h3>
                <p className="text-gray-600 leading-relaxed font-medium" data-id="element-270">
                  We shape our model around your choices and preferences, giving
                  you full confidence in who you're trusting with your career.
                </p>
              </div>
            </motion.div>

            {/* Full Visibility */}
            <motion.div initial={{
            x: 40,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            ...bounceTransition,
            delay: 0.2
          }} className="group" data-id="element-271">
              <div className="relative p-6 md:p-8 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(96,165,250,0.4)] hover:shadow-[16px_16px_0px_rgba(96,165,250,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-272">
                <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-400 to-cyan-400 border-3 border-gray-800 text-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" data-id="element-273">
                  <Eye className="w-7 h-7" data-id="element-274" />
                </div>
                <h3 className="text-2xl md:text-3xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-275">
                  Full Visibility
                </h3>
                <p className="text-gray-600 leading-relaxed font-medium" data-id="element-276">
                  A seamless, fully transparent process where you can view every
                  step we take — no hidden decisions, no surprises.
                </p>
              </div>
            </motion.div>

            {/* Trust First */}
            <motion.div initial={{
            x: -40,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            ...bounceTransition,
            delay: 0.3
          }} className="group" data-id="element-277">
              <div className="relative p-6 md:p-8 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(167,139,250,0.4)] hover:shadow-[16px_16px_0px_rgba(167,139,250,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-278">
                <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-400 border-3 border-gray-800 text-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" data-id="element-279">
                  <Handshake className="w-7 h-7" data-id="element-280" />
                </div>
                <h3 className="text-2xl md:text-3xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-281">
                  Trust First
                </h3>
                <p className="text-gray-600 leading-relaxed font-medium" data-id="element-282">
                  Trust isn't just a value — it's the foundation of everything
                  we do. It's the first factor we build every relationship upon.
                </p>
              </div>
            </motion.div>

            {/* Your Confidence */}
            <motion.div initial={{
            x: 40,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            ...bounceTransition,
            delay: 0.4
          }} className="group" data-id="element-283">
              <div className="relative p-6 md:p-8 rounded-3xl bg-white border-4 border-gray-800 shadow-[12px_12px_0px_rgba(255,211,61,0.4)] hover:shadow-[16px_16px_0px_rgba(255,211,61,0.5)] hover:translate-x-[-4px] hover:translate-y-[-4px] transition-all duration-300" data-id="element-284">
                <div className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-400 border-3 border-gray-800 text-white group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" data-id="element-285">
                  <ShieldCheck className="w-7 h-7" data-id="element-286" />
                </div>
                <h3 className="text-2xl md:text-3xl font-fredoka font-bold text-gray-800 mb-3" data-id="element-287">
                  Your Confidence
                </h3>
                <p className="text-gray-600 leading-relaxed font-medium" data-id="element-288">
                  We give you complete control and clarity so you can focus on
                  what you do best — creating — while knowing your career is in
                  trusted hands.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Changed to blue/cyan theme */}
      <section className="py-16 md:py-32 px-4 md:px-8 relative overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50" data-id="element-289">
        {/* Decorative blob */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-200 blob opacity-30" data-id="element-290" />

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-291">
          <motion.div initial={{
          y: 30,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={bounceTransition} data-id="element-292">
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-blue-300 to-cyan-300 border-3 border-gray-800 text-gray-800 font-bold text-sm tracking-wider uppercase shadow-[4px_4px_0px_rgba(45,55,72,0.3)] mb-8" data-id="element-293">
              <Sparkles className="w-5 h-5" data-id="element-294" />
              Ready to Grow?
            </span>

            <h2 className="text-4xl md:text-6xl lg:text-7xl font-fredoka font-bold text-gray-800 mb-6 leading-tight" data-id="element-295">
              Let's Build Your{' '}
              <span className="text-gradient-playful" data-id="element-296">Legacy Together</span>{' '}
              <span className="inline-block text-5xl md:text-7xl animate-bounce-gentle" data-id="element-297">
                🚀
              </span>
            </h2>

            <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto mb-12 font-bold leading-relaxed" data-id="element-298">
              Join the talents who've transformed their passion into thriving
              careers. Your journey to sustainable success starts here.
            </p>
          </motion.div>

          <motion.div initial={{
          y: 30,
          opacity: 0
        }} whileInView={{
          y: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          ...bounceTransition,
          delay: 0.2
        }} data-id="element-299">
            <a href="mailto:hello@horizonima.com" className="btn-playful inline-flex items-center gap-3 group" data-id="element-300">
              <Mail className="w-5 h-5 group-hover:rotate-12 transition-transform" data-id="element-301" />
              <span className="text-lg" data-id="element-302">Get In Touch</span>
              <Sparkles className="w-5 h-5 group-hover:scale-125 transition-transform" data-id="element-303" />
            </a>
          </motion.div>
        </div>
      </section>
    </Layout>;
}